/**
 * MCQ Helper Functions
 * Shared utilities for parsing, validating, and processing MCQ questions
 */

/**
 * Extract MCQ question numbers from a range string
 * @param {string} range - Range string like "17-18" or single number "5"
 * @returns {string[]} Array of question numbers ["17", "18"] or ["5"]
 */
export function expandQuestionRange(range) {
  if (!range) return [];
  
  if (/^\d+$/.test(range.trim())) {
    // Single number
    return [range.trim()];
  }
  
  const rangeMatch = range.trim().match(/^(\d+)-(\d+)$/);
  if (rangeMatch) {
    const start = parseInt(rangeMatch[1]);
    const end = parseInt(rangeMatch[2]);
    if (start <= end) {
      const numbers = [];
      for (let i = start; i <= end; i++) {
        numbers.push(i.toString());
      }
      return numbers;
    }
  }
  
  return [];
}

/**
 * Parse MCQ container element and extract question data
 * @param {HTMLElement} mcqContainer - The .mcq-container DOM element
 * @returns {Object|null} MCQ data object or null
 */
export function parseMCQContainer(mcqContainer) {
  if (!mcqContainer || !mcqContainer.classList.contains('mcq-container')) {
    return null;
  }
  
  const mcqType = mcqContainer.getAttribute('data-mcq-type') || 'single';
  const questionRange = mcqContainer.getAttribute('data-question-range') || '';
  const requiredCountAttr = mcqContainer.getAttribute('data-required-count');
  const correctAnswersAttr = mcqContainer.getAttribute('data-correct-answers') || '[]';
  
  let correctAnswers = [];
  try {
    correctAnswers = JSON.parse(correctAnswersAttr);
  } catch (e) {
    console.error('Error parsing correct answers:', e);
  }
  
  const questionTextElement = mcqContainer.querySelector('.mcq-question-text');
  const questionText = questionTextElement?.textContent?.trim() || '';
  
  const optionElements = mcqContainer.querySelectorAll('.mcq-option');
  const options = Array.from(optionElements).map((opt, index) => {
    const value = opt.getAttribute('data-option-value') || String.fromCharCode(65 + index);
    const text = opt.textContent?.trim() || '';
    return { value, text };
  });
  
  const questionNumbers = mcqType === 'multiple' && questionRange 
    ? expandQuestionRange(questionRange)
    : [];
  
  return {
    mcqType,
    questionText,
    options,
    correctAnswers,
    questionRange,
    requiredCount: requiredCountAttr ? parseInt(requiredCountAttr, 10) : undefined,
    questionNumbers
  };
}

/**
 * Get correct answer for a specific question number in a multiple-answer MCQ
 * @param {string} questionNumber - Question number like "17"
 * @param {string} questionRange - Range like "17-18"
 * @param {string[]} correctAnswersArray - Array of correct answers like ["B", "D"]
 * @returns {string|null} Correct answer for the question number
 */
export function getCorrectAnswerForQuestion(questionNumber, questionRange, correctAnswersArray) {
  if (!questionRange || !correctAnswersArray || correctAnswersArray.length === 0) {
    return null;
  }
  
  const rangeParts = questionRange.split('-');
  if (rangeParts.length !== 2) {
    return null;
  }
  
  const start = parseInt(rangeParts[0]);
  const qNumInt = parseInt(questionNumber);
  const index = qNumInt - start;
  
  if (index >= 0 && index < correctAnswersArray.length) {
    return correctAnswersArray[index];
  }
  
  return null;
}

/**
 * Map selected options to question numbers in order
 * @param {string[]} selectedOptions - Array of selected option values like ["B", "D"]
 * @param {string[]} questionNumbers - Array of question numbers like ["17", "18"]
 * @returns {Object} Object mapping question numbers to answers { "17": "B", "18": "D" }
 */
export function mapOptionsToQuestionNumbers(selectedOptions, questionNumbers) {
  const mapping = {};
  questionNumbers.forEach((qNum, index) => {
    if (index < selectedOptions.length) {
      mapping[qNum] = selectedOptions[index];
    }
  });
  return mapping;
}

/**
 * Validate MCQ answer selection for multiple-answer MCQs
 * @param {Object} selectedAnswers - Object with question numbers as keys { "17": "B", "18": "D" }
 * @param {number} requiredCount - Required number of answers
 * @returns {Object} { isValid: boolean, error: string }
 */
export function validateMultipleMCQSelection(selectedAnswers, requiredCount) {
  const selectedCount = Object.values(selectedAnswers).filter(ans => ans && ans !== '').length;
  
  if (requiredCount && selectedCount > requiredCount) {
    return {
      isValid: false,
      error: `You can only select ${requiredCount} answer${requiredCount > 1 ? 's' : ''}. Please deselect some options.`
    };
  }
  
  return { isValid: true, error: null };
}

/**
 * Calculate score for multiple-answer MCQ based on IELTS marking rules
 * @param {Object} userAnswers - User's selected answers { "17": "B", "18": "D" }
 * @param {string} questionRange - Range like "17-18"
 * @param {string[]} correctAnswers - Correct answers array like ["B", "D"]
 * @param {number} requiredCount - Required number of answers
 * @returns {Object} { score: number, details: Object }
 */
export function calculateMultipleMCQScore(userAnswers, questionRange, correctAnswers, requiredCount) {
  const questionNumbers = expandQuestionRange(questionRange);
  const selectedCount = Object.values(userAnswers).filter(ans => ans && ans !== '').length;
  
  // Rule 1: If selected more than required, score = 0
  if (requiredCount && selectedCount > requiredCount) {
    return {
      score: 0,
      details: {
        reason: 'Selected more than required answers',
        selectedCount,
        requiredCount
      }
    };
  }
  
  // Rule 2: Count correct selections (partial credit)
  let correctCount = 0;
  questionNumbers.forEach((qNum, index) => {
    const userAnswer = userAnswers[qNum];
    const correctAnswer = correctAnswers[index];
    if (userAnswer && correctAnswer && userAnswer.toLowerCase() === correctAnswer.toLowerCase()) {
      correctCount++;
    }
  });
  
  return {
    score: correctCount,
    details: {
      correctCount,
      totalRequired: requiredCount || questionNumbers.length,
      selectedCount
    }
  };
}
